import React from 'react';
import propTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import FontAwesome from 'react-fontawesome';

import config from '~/Config';

import LoadingMessage from '~/Components/Global/Loading/LoadingMessage';
import Spinner from '~/Components/Global/Loading/Spinner';
import { outlookCallback, updateOutlookAccount } from '~/Redux/Modules/Outlook';
import history from '~/Providers/History';
import { guid, useMountEffect } from '~/util';
import CalendarList from './CalendarList';
import AddAccountForm from './AddAccountForm';

const MicrosoftLogo = require('./Microsoft.png');
const MicrosoftButton = require('./MicrosoftSignInButton.png');

const authEndpoint = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize?';
const { appId, redirectUri, scopes, groupScope } = config.outlook;

const AddOutlookAccount = ({ account, isFetching, match }) => {
  const dispatch = useDispatch();
  const { isRegistering = isFetching, isUpdating } = useSelector(({ Outlook }) => Outlook);

  const addAccount = data => dispatch(outlookCallback(data));
  const updateAccount = data => dispatch(updateOutlookAccount(account, data));

  const handleCode = code => {
    let additionalFlags = {};
    if (redirectUri == 'https://beta.artfulagenda.com') {
      additionalFlags = { beta: true };
    } else if (redirectUri == 'https://media.artfulagenda.com') {
      additionalFlags = { media: true };
    }
    if (account) {
      updateAccount({ code, ...additionalFlags });
    } else {
      addAccount({ code, ...additionalFlags });
    }
    history.push('/add-accounts');
  };

  const buildAuthUrl = withGroups => {
    // Generate random values for state and nonce
    const authState = account?.id ?? 'new-account';
    const authNonce = guid();
    let scope = scopes;
    if (withGroups) scope = `${scopes}${groupScope}`;

    var authParams = {
      response_type: 'code',
      client_id: appId,
      redirect_uri: redirectUri,
      scope,
      state: authState,
      nonce: authNonce,
      response_mode: 'query',
    };

    return `${authEndpoint}response_type=${authParams.response_type}&client_id=${authParams.client_id}&redirect_uri=${authParams.redirect_uri}&scope=${authParams.scope}&state=${authParams.state}&nonce=${authParams.nonce}&response_mode=${authParams.response_mode}`;
  };

  const outlookURL = buildAuthUrl();
  const enableGroupUrl = buildAuthUrl(true);

  useMountEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const state = search.get('state');
    if (
      match.params?.code &&
      ((!account && state == 'new-account') || (account && account.id == state))
    ) {
      handleCode(match.params.code);
    }
  });

  if (isFetching) return <LoadingMessage small messages="Checking for Outlook account" />;
  return (
    <AddAccountForm
      logo={MicrosoftLogo}
      account={account}
      imgStyle={{ width: 'auto', paddingLeft: '1.0rem' }}
      imgText="Microsoft"
      fetching={isRegistering}
    >
      {!account ? (
        <div className="center">
          {isRegistering ? (
            <img src={MicrosoftButton} />
          ) : (
            <a className="connect-to-outlook" href={outlookURL}>
              <img src={MicrosoftButton} />
            </a>
          )}

          {isRegistering && <Spinner />}
        </div>
      ) : (
        <div className="center gray">
          <h6>Success!</h6>
          <h5>{account.email}</h5>
          <h6>is now connected to Artful Agenda!</h6>
          <CalendarList account={account} />
          <a className="update-account" href={outlookURL} disabled={isUpdating}>
            {isUpdating && <Spinner />}
            {account.error_count > 0 && !isUpdating && <FontAwesome name="exclamation-triangle" />}
            Refresh Microsoft Account Credentials
          </a>
          <a className="update-account" href={enableGroupUrl} disabled={isUpdating}>
            {isUpdating && <Spinner />}
            Enable Group Calendars
            <br />
            (may require admin approval)
          </a>
          <a
            target="_blank"
            className="add-calendar"
            href="https://outlook.live.com/calendar/0/addcalendar"
          >
            Create Calendar
          </a>
          <a target="_blank" className="manage-calendars" href="https://outlook.live.com/calendar/">
            Manage Calendars &amp; Sharing
          </a>
        </div>
      )}
    </AddAccountForm>
  );
};

AddOutlookAccount.propTypes = {
  account: propTypes.object,
  addAccount: propTypes.func.isRequired,
  updateAccount: propTypes.func.isRequired,
  isFetching: propTypes.bool.isRequired,
  isRegistering: propTypes.bool.isRequired,
  isUpdating: propTypes.bool.isRequired,
  match: propTypes.object,
};

export default AddOutlookAccount;
